import {
  createContext,
  useMemo,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";

// API calls
import {
  getPayoutTimesheetData,
  getWorkerTimesheetSignature,
} from "../../services/api/timekeeping";
import { getUser } from "../../services/api/core";

export const PayoutTimesheetContext = createContext(null);

export function usePayoutTimesheetContext() {
  const context = useContext(PayoutTimesheetContext);
  if (!context) {
    throw new Error(
      "usePayoutTimesheetContext must be used within a PayoutTimesheetProvider"
    );
  }
  return context;
}

export function usePayoutTimesheetCtx(WorkerID = null, weekEnding = null) {
  // Shared int context as it is edited and submitted by different components
  const [worker, setWorker] = useState(null);
  const [payoutTimesheetDays, setPayoutTimesheetDays] = useState([]);
  const [workerSignature, setWorkerSignature] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [payoutCommentText, setPayoutCommentText] = useState("");
  const hoursConflicts = useRef(null);
  const missingLogs = useRef(null);
  const payoutComments = useRef(null);
  const [currentWorkerID, setCurrentWorkerID] = useState(WorkerID || 1);
  const [currentWeekEnding, setCurrentWeekEnding] = useState(
    weekEnding || "2025-08-16"
  );

  // Fetch weekly hours logs, to be compared to worker timesheet hours
  const fetchWorker = useCallback(async () => {
    try {
      const response = await getUser(currentWorkerID);
      setWorker(response.user || null);
    } catch (error) {
      console.error("Error fetching worker:", error);
    }
  }, [currentWorkerID]);

  // Fetch the worker timesheet, hours log suggestions, and payout values for the week.
  const fetchPayoutTimesheetData = useCallback(async () => {
    try {
      const response = await getPayoutTimesheetData(
        currentWorkerID,
        currentWeekEnding
      );
      setPayoutTimesheetDays(response.shifts || []);
      setReviewStatus(response.reviewStatus || null);
      const managerComments = response.managerComments || "";
      payoutComments.current = managerComments;
      setPayoutCommentText(managerComments);
      hoursConflicts.current = response.conflicts || false;
      missingLogs.current = response.missingLogs || false;
    } catch (error) {
      console.error("Error fetching payout timesheet data:", error);
      return null;
    }
  }, [currentWorkerID, currentWeekEnding]);

  // Fetch the worker signature for payout review.
  const fetchWorkerSignature = useCallback(async () => {
    try {
      const response = await getWorkerTimesheetSignature(
        currentWorkerID,
        currentWeekEnding
      );
      setWorkerSignature(response.signature || null);
    } catch (error) {
      console.error("Error fetching worker signature:", error);
    }
  }, [currentWorkerID, currentWeekEnding]);

  // Fetch the async data when the component mounts or when WorkerID/weekEnding changes
  useEffect(() => {
    if (currentWorkerID && currentWeekEnding) {
      fetchPayoutTimesheetData();
      fetchWorkerSignature();
      fetchWorker();
    }
  }, [
    currentWorkerID,
    currentWeekEnding,
    fetchPayoutTimesheetData,
    fetchWorkerSignature,
    fetchWorker,
  ]); // Re-fetch when WorkerID or weekEnding changes

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      payoutTimesheetDays,
      setPayoutTimesheetDays,
      hoursConflicts,
      missingLogs,
      payoutComments,
      payoutCommentText,
      setPayoutCommentText,
      reviewStatus,
      weekEnding: currentWeekEnding,
      setCurrentWeekEnding,
      workerSignature,
      setCurrentWorkerID,
      worker,
    }),
    [
      payoutTimesheetDays,
      setPayoutTimesheetDays,
      hoursConflicts,
      missingLogs,
      payoutComments,
      payoutCommentText,
      setPayoutCommentText,
      reviewStatus,
      currentWeekEnding,
      workerSignature,
      worker,
    ]
  );

  return {
    ...contextValue,
  };
}

export default usePayoutTimesheetCtx;
